import React from 'react';
import { Icon } from '@iconify/react';
import config from '../../config';
import { formatDisplayDate } from '../../utils/formatDate';

const CategoryIconCell = ({ item }) => {
    const [imgFailed, setImgFailed] = React.useState(false);
    
    const iconUrl = React.useMemo(() => {
        if (!item?.icon) return null;
        if (item.icon.startsWith('http')) return item.icon;
        const base = (config.API_BASE_URL || '').replace(/\/+$/, '');
        const cleanPath = item.icon.startsWith('/') ? item.icon : `/${item.icon}`;
        return `${base}${cleanPath}`;
    }, [item?.icon]);

    if (!iconUrl || imgFailed) {
        return (
            <div className="w-40-px h-40-px rounded-10 bg-primary-50 d-flex align-items-center justify-content-center text-primary-600 border border-primary-100 fw-bold text-md shadow-xs">
                {item?.name ? item.name.charAt(0).toUpperCase() : 'C'}
            </div>
        );
    }

    return (
        <div className="w-40-px h-40-px rounded-10 border bg-white d-flex align-items-center justify-content-center overflow-hidden p-1 shadow-xs">
            <img 
                src={iconUrl} 
                alt={item.name} 
                className="w-100 h-100 object-fit-contain"
                onError={() => setImgFailed(true)} 
            />
        </div>
    );
};

const CategoryTable = ({ categories, loading, onEdit, onDelete, onSort, sortConfig }) => {

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading categories...</span>
                </div>
                <p className="text-secondary mt-2">Loading categories...</p>
            </div>
        );
    }

    if (!categories || categories.length === 0) {
        return (
            <div className="hs-empty-state py-40">
                <div className="hs-empty-state-icon">
                    <Icon icon="solar:folder-open-bold-duotone" />
                </div>
                <div className="hs-empty-state-title">No categories found</div>
                <div className="hs-empty-state-text">There are no categories matching your filter criteria.</div>
            </div>
        );
    }

    const renderSortIcon = (key) => {
        if (sortConfig.key !== key) return <Icon icon="solar:sort-vertical-linear" className="ms-1 text-xs opacity-50" />;
        return sortConfig.direction === 'asc' 
            ? <Icon icon="solar:alt-arrow-up-linear" className="ms-1 text-xs text-primary-600" />
            : <Icon icon="solar:alt-arrow-down-linear" className="ms-1 text-xs text-primary-600" />;
    };

    return (
        <div className="hs-table-wrapper">
            <table className="hs-table">
                <thead>
                    <tr>
                        <th style={{ width: '80px' }}>Icon</th>
                        <th onClick={() => onSort('name')} className="cursor-pointer">
                            Category Name {renderSortIcon('name')}
                        </th>
                        <th onClick={() => onSort('slug')} className="cursor-pointer">
                            Slug Identifier {renderSortIcon('slug')}
                        </th>
                        <th onClick={() => onSort('is_default')} className="cursor-pointer text-center">
                            Type {renderSortIcon('is_default')}
                        </th>
                        <th onClick={() => onSort('venue_count')} className="cursor-pointer text-center">
                            Venues {renderSortIcon('venue_count')}
                        </th>
                        <th onClick={() => onSort('created_at')} className="cursor-pointer text-center">
                            Created Date {renderSortIcon('created_at')}
                        </th>
                        <th className="text-center" style={{ width: '120px' }}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {categories.map((item) => {
                        const venueCount = Number(item.venue_count || 0);
                        const deletionBlocked = Boolean(item.is_default) || venueCount > 0;
                        const deleteTitle = item.is_default
                            ? 'Default categories cannot be deleted'
                            : venueCount > 0
                                ? `Used by ${venueCount} ${venueCount === 1 ? 'venue' : 'venues'}; reassign them first`
                                : 'Delete Category';

                        return (
                            <tr key={item.id}>
                            <td>
                                <CategoryIconCell item={item} />
                            </td>
                            <td>
                                <h6 className="text-sm fw-semibold text-dark mb-0">{item.name}</h6>
                                {item.description && <div className="text-xs text-secondary-light mt-1">{item.description}</div>}
                            </td>
                            <td>
                                <code className="text-xs bg-light px-2 py-1 rounded text-secondary-dark">{item.slug}</code>
                            </td>
                            <td className="text-center">
                                {item.is_default ? (
                                    <span className="hs-badge hs-badge-active">
                                        <span className="hs-badge-dot"></span>
                                        Default
                                    </span>
                                ) : (
                                    <span className="hs-badge hs-badge-info">
                                        <span className="hs-badge-dot"></span>
                                        Custom
                                    </span>
                                )}
                            </td>
                            <td className="text-center">
                                <span className={`hs-badge ${venueCount > 0 ? 'hs-badge-info' : ''}`}>
                                    {venueCount}
                                </span>
                            </td>
                            <td className="text-center text-sm text-secondary-light">
                                {formatDisplayDate(item.created_at)}
                            </td>
                            <td className="text-center">
                                <div className="d-flex align-items-center justify-content-center gap-2">
                                    <button 
                                        type="button" 
                                        onClick={() => onEdit(item)}
                                        className="hs-icon-btn hs-btn-sm"
                                        title="Edit Category"
                                    >
                                        <Icon icon="solar:pen-bold" className="text-white" />
                                    </button>
                                    <button 
                                        type="button" 
                                        onClick={() => onDelete(item)}
                                        className="hs-icon-btn hs-btn-sm"
                                        title={deleteTitle}
                                        aria-label={deleteTitle}
                                        disabled={deletionBlocked}
                                    >
                                        <Icon icon="solar:trash-bin-trash-bold" className="text-white" />
                                    </button>
                                </div>
                            </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default CategoryTable;
